import type { PublicBatchDetail } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface BatchDetailData {
	batch: PublicBatchDetail | null;
	loading: boolean;
	error: Error | null;
}

/**
 * Fetches full anchor metadata for a batch identified by its merkle root,
 * including chain linkage for chain-of-custody verification.
 */
export function useBatchDetail(root: string): BatchDetailData {
	const client = useApiClient();

	const query = useQuery({
		queryKey: queryKeys.publicVerification.batches.detail(root),
		queryFn: async () => {
			const result = await client.publicVerification.getBatchDetail({
				params: { root },
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error(
				result.status === 404 ? "Batch not found" : "Failed to fetch batch",
			);
		},
		enabled: root.length > 0,
	});

	return {
		batch: query.data ?? null,
		loading: query.isLoading,
		error: query.error,
	};
}
